import { Box, Button, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { MainBorderRadius, TableBgcolor, TableHeader } from '../../../app/AppStyles';
import { GetDate, GetTime, SearchKeyFilter } from '../../../app/AppUtils';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import InformationEditCard from './InformationEditCard';
import { deleteInformationArticle, fetchInformationArticles, resetInformationArticle, resetInformationArticleManagementStatus } from './informationManagementSlice';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';

export default function InformationManagementTab() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [openNew, setOpenNew] = useState(false);
    const [filter, setFilter] = useState("");

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { informationArticles, informationArticleDelete } = useSelector((state) => state.management.information);

    useEffect(() => {
        dispatch(fetchInformationArticles());
        dispatch(resetInformationArticle());
    }, []);

    // Delete status callback
    useEffect(() => {
        if (informationArticleDelete.status === "success") {
            dispatch(resetInformationArticleManagementStatus());
            dispatch(fetchInformationArticles());
        } else if (informationArticleDelete.status === "failed") {
            dispatch(resetInformationArticleManagementStatus());
        };
    }, [dispatch, informationArticleDelete.status]);

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["title"], filter);
    }

    const newClick = () => {
        setOpenNew(true);
    };

    const callbackModalNew = () => {
        setOpenNew(false);
        setFilter("");
        dispatch(resetInformationArticleManagementStatus());
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - informationArticles.data?.filter(handleFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const handleSelectClick = (informationArticleId) => {
            navigate(`/webbeheer/informatie/${informationArticleId}`);
        };

        const handleDeleteClick = (informationArticleId) => {
            dispatch(deleteInformationArticle(informationArticleId));
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? informationArticles.data?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : informationArticles.data?.filter(handleFilterResult)).map(item =>
                        <TableRow key={item.informationArticleId} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="center">
                                <Link to={`/webbeheer/informatie/${item.informationArticleId}`} style={{ color: 'black' }}>
                                    {item.title}
                                </Link>
                            </TableCell>
                            <TableCell align="center">{item.number}</TableCell>
                            <TableCell align="center">{item.isPublished ? resourceFinal[appLanguage].words.YES : resourceFinal[appLanguage].words.NO}</TableCell>
                            <TableCell align="center">{item.createdBy}</TableCell>
                            <TableCell align="center">{`${GetDate(item.dateCreated)} (${GetTime(item.dateCreated)})`}</TableCell>
                            <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${item.title} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => handleDeleteClick(item.informationArticleId)} />
                            </TableCell>
                            <TableCell align="center" padding="checkbox">
                                <IconButton onClick={() => handleSelectClick(item.informationArticleId)}><ArrowForwardIosIcon color="secondary" /></IconButton>
                            </TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? informationArticles.data?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : informationArticles.data?.filter(handleFilterResult)).length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[5, 10, 25,
                                {
                                    label: informationArticles.data?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: informationArticles.data?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={informationArticles.data?.filter(handleFilterResult)?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <ContentLoader
                successCondition={informationArticles.status === "success"}
                errorCondition={informationArticles.status === "failed"}
                content={
                    <Stack sx={{ display: 'flex', flex: '1 1 auto', bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                            <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                            <Box>
                                <Button variant="outlined" onClick={newClick} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                            </Box>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.TITLE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NUMBER}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PUBLISHED}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.CREATED_BY}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DATE_CREATED}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    </Stack >
                }
            />

            <Modal open={openNew} onClose={callbackModalNew}>
                <>
                    <InformationEditCard handleClose={callbackModalNew} addNew />
                </>
            </Modal >
        </>
    );
}

